import { useState, useEffect } from "react";
import { Eye, Pencil, X, Save, Plus, Trash, TextAlignStart } from "lucide-react";
import ReactQuill from "../ui/RichText";
import { useCreateLetters, useGetLetters, useUpdateLetter, useDeleteLetter, useSendLetters } from "../Use-auth";
import SkeletonLetters from "./SkeletonLetters";
import Button from "./Button";

export default function LetterPage() {
    const [open, setOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(null);
    const [editedContent, setEditedContent] = useState("");
    const [sendEmail, setSendEmail] = useState("");
    const [sending, setSending] = useState(false);
    const [sendOpen, setSendOpen] = useState(false);
    const [sendIndex, setSendIndex] = useState(null);

    const [showAddModal, setShowAddModal] = useState(false);
    const [newLetter, setNewLetter] = useState({
        title: "",
        content: "",
        logo: "https://www.hubsyntax.com/uploads/logo%201%20(5).png",
        department: "HR Department",
        company: "HubSyntax",
        file: null,
    });

    const {
        data: fetchedTemplates = [],
        isLoading,
        refetch
    } = useGetLetters();
    const [templates, setTemplates] = useState([]);
    const createLetter = useCreateLetters();
    const updateLetter = useUpdateLetter();
    const deleteLetter = useDeleteLetter();
    const sendLetter = useSendLetters();

    useEffect(() => {
        setTemplates(fetchedTemplates);
    }, [fetchedTemplates]);

    const handleView = (index) => {
        setSelectedIndex(index);
        setEditMode(false);
        setOpen(true);
    };

    const handleEdit = (index) => {
        setSelectedIndex(index);
        setEditedContent(templates[index].content);
        setEditMode(true);
        setOpen(true);
    };

    const handleSave = () => {
        if (selectedIndex === null) return;

        const letterToUpdate = templates[selectedIndex];
        const formData = new FormData();
        formData.append("title", letterToUpdate.title);
        formData.append("content", editedContent);
        formData.append("department", letterToUpdate.department);
        formData.append("company", letterToUpdate.company);

        if (letterToUpdate.file) formData.append("logo", letterToUpdate.file);

        updateLetter.mutate(
            { id: letterToUpdate.id, formData },
            {
                onSuccess: () => {
                    setOpen(false);
                    refetch();
                },
                onError: (err) => {
                    console.error("Error saving letter:", err);
                },
            }
        );
    };

    const handleAddNew = async () => {
        if (!newLetter.title || !newLetter.content) {
            alert("Title and Content are required");
            return;
        }

        try {
            const formData = new FormData();
            formData.append("title", newLetter.title);
            formData.append("content", newLetter.content);
            formData.append("department", newLetter.department);
            formData.append("company", newLetter.company);
            if (newLetter.file) formData.append("logo", newLetter.file);

            await createLetter.mutateAsync(formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            setShowAddModal(false);
            setNewLetter({
                title: "",
                content: "",
                logo: "https://www.hubsyntax.com/uploads/logo%201%20(5).png",
                department: "HR Department",
                company: "HubSyntax",
                file: null,
            });

            refetch();
        } catch (err) {
            console.error("Error adding new letter:", err);
        }
    };

    const handleDelete = (id) => {
        if (!window.confirm("Are you sure you want to delete this letter?")) return;

        deleteLetter.mutate(id, {
            onSuccess: () => {
                refetch();
            },
            onError: (err) => {
                console.error("Error deleting letter:", err);
            },
        });
    };

    const handleSendEmail = async () => {

        if (!sendEmail) {
            alert("Please enter email");
            return;
        }

        const letter = templates[sendIndex];

        if (!letter) {
            alert("No letter selected");
            return;
        }

        try {
            setSending(true);

            const payload = {
                email: sendEmail,
                title: letter.title || "",
                content: letter.content || "",
                company: letter.company || "HubSyntax",
                department: letter.department || "",
                image: letter.logo || "",
            };

            const res = await sendLetter.mutateAsync(payload);

            alert(res.message || "Email sent");

            setSendEmail("");
            setSendOpen(false);

        } catch (err) {
            console.error(err);
            alert(err.message || "Failed to send");

        } finally {
            setSending(false);
        }
    };


    return (
        <div className="h-[90.7vh] px-2 sm:px-6 py-4 overflow-y-auto">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3 sm:gap-0">
                <h1 className="text-[14px] sm:text-[16px] md:text-[20px] font-semibold">Letters</h1>
                <Button
                    onClick={() => setShowAddModal(true)}
                >
                    <Plus size={16} /> Add New Letter
                </Button>
            </div>

            {/* Letter Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {isLoading
                    ? [...Array(4)].map((_, i) => <SkeletonLetters key={i} />)
                    : templates.map((item, index) => (
                        <div
                            key={index}
                            className="bg-white border rounded-lg p-6 shadow-sm relative flex flex-col"
                        >
                            <div className="flex justify-center">
                                <img src={item.logo} alt="Logo" className="h-7 sm:h-14 mb-4" />
                            </div>

                            <h2 className="text-lg  mb-3 text-center">{item.title}</h2>

                            <div
                                className="text-gray-700 line-clamp-3 mb-4"
                                dangerouslySetInnerHTML={{ __html: item.content }}
                            />

                            <div className="absolute top-4 right-4 flex gap-0">
                                <div
                                    className="p-1 rounded cursor-pointer hover:bg-pink-100"
                                    onClick={() => {
                                        setSendIndex(index);
                                        setSendOpen(true);
                                    }}
                                >
                                    <TextAlignStart size={16} className="text-pink-600" />
                                </div>

                                <div
                                    className="p-1 rounded cursor-pointer hover:bg-teal-100"
                                    onClick={() => handleView(index)}
                                >
                                    <Eye size={16} className="text-teal-600" />
                                </div>

                                <div
                                    className="p-1 rounded cursor-pointer hover:bg-blue-100"
                                    onClick={() => handleEdit(index)}
                                >
                                    <Pencil size={16} className="text-blue-600" />
                                </div>

                                <div
                                    className="p-1 rounded cursor-pointer hover:bg-red-100"
                                    onClick={() => handleDelete(item.id)}
                                >
                                    <Trash size={16} className="text-red-600" />
                                </div>
                            </div>

                            <div className="mt-auto text-sm text-gray-500">
                                <p>{item.department}</p>
                                <p>{item.company}</p>
                            </div>
                        </div>
                    ))}
            </div>

            <div
                className={`fixed inset-0 z-50 flex items-center justify-end bg-black/40 transition-opacity duration-300 ${sendOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
                    }`}
                onClick={() => setSendOpen(false)}
            >
                <div
                    className={`bg-white h-full shadow-xl flex flex-col w-full max-w-sm sm:max-w-md md:max-w-md transform transition-transform duration-300 ease-in-out ${sendOpen ? "translate-x-0" : "translate-x-full"
                        }`}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="flex justify-between items-center px-4 py-3 border-b bg-gray-900">
                        <h3 className="text-white ">Send Letter</h3>
                        <X
                            className="cursor-pointer text-white hover:text-red-400"
                            onClick={() => setSendOpen(false)}
                        />
                    </div>
                    <div className="p-4 overflow-y-auto flex-1">
                        <h2 className="text-lg  text-left mb-3">
                            {templates[sendIndex]?.title}
                        </h2>

                        <div className="mb-3">
                            <label className="block mb-1 text-sm font-medium">Receiver Email</label>
                            <input
                                type="email"
                                placeholder="Enter email"
                                value={sendEmail}
                                onChange={(e) => setSendEmail(e.target.value)}
                                className="w-full p-1 sm:p-3 border rounded sm:rounded-lg focus:ring-2 focus:ring-teal-400 outline-none"
                            />
                        </div>

                        <Button
                            onClick={handleSendEmail}
                            disabled={sending}
                            variant="success"
                        >
                            {sending ? "Sending..." : "Send Letter"}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Edit Letter Modal */}
            {open && selectedIndex !== null && (
                <div
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                    onClick={() => setOpen(false)}
                >
                    <div
                        className="bg-white animate-scaleIn w-full max-w-6xl h-[90vh] overflow-y-auto rounded-md sm:rounded-2xl shadow-lg relative"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="sticky top-0 z-10 flex justify-between items-center bg-gray-900 px-2 py-1 sm:px-6 sm:py-3">
                            <h2 className="text-white text-[12px] sm:text-lg">Edit Letter</h2>
                            <button onClick={() => setOpen(false)} className="text-white hover:text-red-400">
                                <X className="h-4 w-4 sm:h-6 sm:w-6" />
                            </button>
                        </div>

                        <div className="p-2 sm:p-6 pt-4">
                            <div className="flex justify-center">
                                <img
                                    src={templates[selectedIndex].logo}
                                    alt="Logo"
                                    className="w-full max-w-[150px] sm:max-w-[200px] p-[5px] object-cover mb-2 rounded"
                                />
                            </div>
                            <h2 className="text-xl text-center mb-4">{templates[selectedIndex].title}</h2>

                            {editMode ? (
                                <>
                                    <div className="mb-4">
                                        <label className="block mb-1 font-medium text-gray-700 text-[12px] sm:text-lg">Title</label>
                                        <input
                                            type="text"
                                            value={templates[selectedIndex].title}
                                            onChange={(e) => {
                                                const updated = [...templates];
                                                updated[selectedIndex].title = e.target.value;
                                                setTemplates(updated);
                                            }}
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400"
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <label className="block mb-1  font-medium text-gray-700 text-[12px] sm:text-lg">Content</label>
                                        <ReactQuill
                                            theme="snow"
                                            value={editedContent}   
                                            placeholder="Description (optional)"
                                            onChange={(value) => setEditedContent(value)}
                                            className="h-40 border border-gray-300 rounded-lg"
                                        />
                                    </div>

                                    <Button
                                        onClick={handleSave}
                                        variant="success"
                                        className="float-right text-[11px] sm:text-sm my-4 flex items-center justify-center gap-2"
                                    >
                                        <Save size={16} /> Save
                                    </Button>
                                </>
                            ) : (
                                <div className="mb-4" dangerouslySetInnerHTML={{ __html: templates[selectedIndex].content }} />
                            )}

                            <div className="mt-6 text-sm text-gray-500">
                                <p>{templates[selectedIndex].department}</p>
                                <p>{templates[selectedIndex].company}</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Letter Modal */}
            {showAddModal && (
                <div
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                    onClick={() => setShowAddModal(false)}
                >
                    <div
                        className="bg-white w-full max-w-6xl h-[90vh] overflow-y-auto rounded-md sm:rounded-2xl shadow-lg relative animate-scaleIn"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="sticky top-0 z-10 bg-gray-900 p-2 sm:p-6 py-1 sm:py-3 flex justify-between items-center">
                            <h2 className="text-white text-[12px] sm:text-lg">Add New Letter</h2>
                            <button onClick={() => setShowAddModal(false)} className="text-white hover:text-red-400">
                                <X className="h-4 w-4 sm:h-6 sm:w-6" />
                            </button>
                        </div>

                        <div className="p-2 sm:p-6  pt-4">
                            <div className="mb-4 flex justify-center">
                                {newLetter.logo && (
                                    <img
                                        src={newLetter.logo}
                                        alt="Logo Preview"
                                        className="w-full max-w-[150px] sm:max-w-[200px] p-[5px] object-cover mb-2 rounded"
                                    />
                                )}
                            </div>

                            <div className="mb-4">
                                <label className="block mb-1 font-medium text-gray-700 text-[12px] sm:text-lg">Title</label>
                                <input
                                    type="text"
                                    placeholder="Title"
                                    value={newLetter.title}
                                    onChange={(e) => setNewLetter({ ...newLetter, title: e.target.value })}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400"
                                />
                            </div>

                            <div className="mb-4">
                                <label className="block mb-1 font-medium text-gray-700 text-[12px] sm:text-lg">Content</label>
                                <ReactQuill
                                    theme="snow"
                                    value={newLetter.content}
                                    placeholder="Content"
                                    onChange={(content) => setNewLetter({ ...newLetter, content })}
                                    className="h-40 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400"
                                />
                            </div>

                            <div className="mt-6  text-gray-500 text-[12px] sm:text-sm">
                                <p>HR Department</p>
                                <p>HubSyntax</p>
                            </div>

                            <div className="flex justify-end">
                                <Button
                                    onClick={handleAddNew}
                                    variant="success"                                >
                                    <Plus size={16} /> Add Letter
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}